/** Themes available in the application */
type ThemeNames = 'somtoday' | 'parnassys' | 'eduarte' | 'default';

/** Map organization names to theme it should use */
export const OrgToThemeMap: Record<string, ThemeNames | undefined> = {
    somtoday: 'somtoday',
    parnassys: 'parnassys',
    eduarte: 'eduarte',
    default: 'default',
};

/** Nested type to define colors */
export interface ColorNode {
    [key: string]: Record<ThemeNames, string> | ColorNode;
}

/**
 * Styles that define which colors to use for a theme
 * Objects here should match those defined in ../tailwind.config.js
 * For example: settings-menu-item-selected-background in this object is settings.menu-item.selected.background
 */
export const styles: ColorNode = {
    // Globally reused colors
    'global': {
        primary: {
            somtoday: '#005eb8',
            eduarte: '#0B1F8F',
            parnassys: '#E8436E',
            default: '#303030',
        },
        secondary: {
            somtoday: '#005eb8',
            eduarte: '#17B26A',
            parnassys: '#E8436E',
            default: '#303030',
        },
        tertiary: {
            somtoday: '#339bff',
            eduarte: '#4FD1DC',
            parnassys: '#4FD1DC',
            default: '#303030',
        },
        quaternary: {
            somtoday: '#E5F3FF',
            eduarte: '#4FD1DC',
            parnassys: '#0095A4',
            default: '#494949',
            contrast: {
                somtoday: '#003466',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
            },
        },
        warning: {
            somtoday: '#e40b0b',
            eduarte: '#F04438',
            parnassys: '#F5374B',
            default: '#e40b0b',
        },
        // Text
        text: {
            primary: {
                somtoday: '#303030',
                eduarte: '#303030',
                parnassys: '#303030',
                default: '#303030',
            },
            secondary: {
                somtoday: '#797979',
                eduarte: '#797979',
                parnassys: '#797979',
                default: '#797979',
            },
        },
    },
    // Basic components
    'button': {
        primary: {
            background: {
                somtoday: '#3F8541',
                eduarte: '#17B26A',
                parnassys: '#E8436E',
                default: '#303030',
                hover: {
                    somtoday: '#145716',
                    eduarte: '#0E6B40',
                    parnassys: '#A22F4D',
                    default: '#000000',
                },
            },
            text: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
                hover: {
                    somtoday: '#ffffff',
                    eduarte: '#ffffff',
                    parnassys: '#ffffff',
                    default: '#ffffff',
                },
            },
        },
        secondary: {
            background: {
                somtoday: '#005eb8',
                eduarte: '#0B1F8F',
                parnassys: '#E8436E',
                default: '#303030',
                hover: {
                    somtoday: '#004180',
                    eduarte: '#004180',
                    parnassys: '#A22F4D',
                    default: '#000000',
                },
            },
            text: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
                hover: {
                    somtoday: '#fffffff',
                    eduarte: '#ffffff',
                    parnassys: '#fffffff',
                    default: '#ffffff',
                },
            },
        },
        stroked: {
            border: {
                somtoday: '#0068CC',
                eduarte: '#4FD1DC',
                parnassys: '#5F1546',
                default: '#303030',
            },
            text: {
                somtoday: '#0068CC',
                eduarte: '#4FD1DC',
                parnassys: '#5F1546',
                default: '#303030',
            },
        },
        basic: {
            text: {
                somtoday: '#303030',
                eduarte: '#303030',
                parnassys: '#303030',
                default: '#303030',
                hover: {
                    somtoday: '#000000',
                    eduarte: '#000000',
                    parnassys: '#000000',
                    default: '#000000',
                },
            },
        },
    },
    'form-field': {
        border: {
            somtoday: '#cccccc',
            eduarte: '#cccccc',
            parnassys: '#cccccc',
            default: '#cccccc',
        },
    },
    // Custom components
    'toolbar': {
        border: {
            somtoday: '#f2f2f2',
            eduarte: '#f2f2f2',
            parnassys: '#f2f2f2',
            default: '#f2f2f2',
        },
    },
    'user-menu': {
        background: {
            expanded: {
                somtoday: '#cccccc',
                eduarte: '#cccccc',
                parnassys: '#cccccc',
                default: '#cccccc',
            },
        },
    },
    'sidenav': {
        main: {
            background: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ececec',
            },
            text: {
                somtoday: '#303030',
                eduarte: '#303030',
                parnassys: '#303030',
                default: '#303030',
                selected: {
                    somtoday: '#005eb8',
                    eduarte: '#0B1F8F',
                    parnassys: '#E8436E',
                    default: '#303030',
                },
                background: {
                    hover: {
                        somtoday: '#F3F5F6',
                        eduarte: '#F3F5F6',
                        parnassys: '#F3F5F6',
                        default: '#cacaca',
                    },
                },
            },
        },
        right: {
            border: {
                somtoday: '#f2f2f2',
                eduarte: '#f2f2f2',
                parnassys: '#f2f2f2',
                default: '#f2f2f2',
            },
        },
        top: {
            background: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
            },
            border: {
                somtoday: '#f2f2f2',
                eduarte: '#f2f2f2',
                parnassys: '#f2f2f2',
                default: '#f2f2f2',
            },
            text: {
                somtoday: '#005eb8',
                eduarte: '#0B1F8F',
                parnassys: '#5F1546',
                default: '#303030',
            },
        },

    },
    'chat': {
        'selected-collection': {
            background: {
                somtoday: '#e5f3ff',
                eduarte: '#4FD1DC',
                parnassys: '#0095A4',
                default: '#494949',
            },
            text: {
                somtoday: '#000000',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
            },
        },
        'prompt': {
            background: {
                somtoday: '#f2f2f2',
                eduarte: '#f2f2f2',
                parnassys: '#f2f2f2',
                default: '#f2f2f2',
            },
        },
        'input': {
            background: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
            },
            border: {
                somtoday: '#cccccc',
                eduarte: '#cccccc',
                parnassys: '#cccccc',
                default: '#cccccc',
            },
            placeholder: {
                somtoday: '#797979',
                eduarte: '#797979',
                parnassys: '#797979',
                default: '#797979',
            },
            document: {
                border: {
                    somtoday: '#bbc3ce',
                    eduarte: '#bbc3ce',
                    parnassys: '#bbc3ce',
                    default: '#bbc3ce',
                },
            },
        },
        'sources': {
            border: {
                color: {
                    somtoday: '#f2f2f2',
                    eduarte: '#f2f2f2',
                    parnassys: '#f2f2f2',
                    default: '#f2f2f2',
                },
                width: {
                    somtoday: '0px',
                    eduarte: '0px',
                    parnassys: '0px',
                    default: '0px',
                },
            },
        },
    },
    'collections': {
        'list': {
            'background': {
                somtoday: '#fffffff',
                eduarte: '#fffffff',
                parnassys: '#fffffff',
                default: '#f7f7f7',
            },
            'border-right': {
                somtoday: '#f2f2f2',
                eduarte: '#f2f2f2',
                parnassys: '#f2f2f2',
                default: '#ffffff',
            },
        },
        'actions': {
            'top-bar': {
                background: {
                    somtoday: '#f2f2f2',
                    eduarte: '#f2f2f2',
                    parnassys: '#f2f2f2',
                    default: '#e2e2e2',
                },
            },
        },
        'warning-card': {
            background: {
                somtoday: '#f0f0f0',
                eduarte: '#f0f0f0',
                parnassys: '#f0f0f0',
                default: '#f0f0f0',
            },
        },
        'collection-card': {
            selected: {
                background: {
                    somtoday: '#e5f3ff',
                    eduarte: '#EDFAFC',
                    parnassys: '#EBF4F6',
                    default: '#dddddd',
                },
                border: {
                    somtoday: '#005eb8',
                    eduarte: '#4FD1DC',
                    parnassys: '#0095A4',
                    default: '#303030',
                },
            },
        },
    },
    'settings': {
        'background': {
            somtoday: '#f5f5f5',
            eduarte: '#f5f5f5',
            parnassys: '#f5f5f5',
            default: '#f5f5f5',
        },
        'menu-item': {
            selected: {
                background: {
                    somtoday: '#F3F5F6',
                    eduarte: '#F3F5F6',
                    parnassys: '#F3F5F6',
                    default: '#cccccc',
                },
            },
            hover: {
                background: {
                    somtoday: '#F3F5F6',
                    eduarte: '#F3F5F6',
                    parnassys: '#F3F5F6',
                    default: '#eeeeee',
                },
            },
        },
    },
    // Mobile
    'mobile': {
        'bottom-bar': {
            background: {
                somtoday: '#005eb8',
                eduarte: '#0B1F8F',
                parnassys: '#5F1546',
                default: '#ececec',
            },
            button: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#303030',
            },
        },
        'user-menu': {
            text: {
                somtoday: '#ffffff',
                eduarte: '#ffffff',
                parnassys: '#ffffff',
                default: '#ffffff',
            },
            background: {
                somtoday: '#7b7b7b',
                eduarte: '#7b7b7b',
                parnassys: '#7b7b7b',
                default: '#7b7b7b',
            },
        },
    },
    'font-family': {
        somtoday: 'OpenSans, sans-serif',
        eduarte: 'Inter, sans-serif',
        parnassys: 'Ubuntu, sans-serif',
        default: 'OpenSans, sans-serif',
    },
};
